import { Attachment, Message } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { showShareHeaderButton } from 'chat/shared/header_share_button'
import { getGroupedMessageAttachments } from 'chat/shared/util'
import { FileScreenProps } from 'home/stack/types'
import { isUndefined } from 'lodash'
import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderTitle } from 'shared/header/header_title'
import { shareAttachment } from 'shared/utils'
import { useNonDeletedMessages } from '../queries/list'
import { usePinnedMessages } from '../queries/list_pins'
import { FileView } from './view'

export function FileScreen({ navigation, route }: FileScreenProps) {
  const {
    conversationId,
    isPinnedMessage,
    messageId,
    attachmentIndex: attachmentIndexParam,
    uri,
  } = route.params
  const { theme } = useTheme()
  const [isShareLoading, setShareLoading] = useState(false)

  const [attachmentIndex, setAttachmentIndex] = useState<number | undefined>(
    attachmentIndexParam
  )
  const [url, setUrl] = useState<string | undefined>(uri)

  const filterChatGroupPhotoVideoAttachments = useCallback(
    (messages: Message[] = []) => {
      if (messageId) {
        const groupedAttachments = getGroupedMessageAttachments(
          messageId,
          messages
        )

        return groupedAttachments?.filter(
          attachment => !isPdfAttachment(attachment)
        )
      }
      return messages
        .filter(
          message =>
            message.conversationId === conversationId && !!message.attachment
        )
        .flatMap(message => message.attachment)
        .filter(attachment => attachment && !isPdfAttachment(attachment))
    },
    [conversationId, messageId]
  )
  const pinnedMessagesQuery = usePinnedMessages(conversationId, isPinnedMessage)
  const pinnedChatGroupPhotoVideoAttachments =
    filterChatGroupPhotoVideoAttachments(
      pinnedMessagesQuery.data?.pinnedMessages
    )

  const {
    data: chatGroupPhotoVideoAttachments,
    error: chatGroupPhotoVideoAttachmentsError,
    isLoading: chatGroupPhotoVideoAttachmentsInitialLoading,
  } = useNonDeletedMessages(messagesData =>
    filterChatGroupPhotoVideoAttachments(messagesData.messages)
  )
  const photoVideoAttachments = useMemo(
    () =>
      isPinnedMessage
        ? pinnedChatGroupPhotoVideoAttachments
        : chatGroupPhotoVideoAttachments,
    [
      chatGroupPhotoVideoAttachments,
      isPinnedMessage,
      pinnedChatGroupPhotoVideoAttachments,
    ]
  )
  const photoVideoAttachmentsError = isPinnedMessage
    ? pinnedMessagesQuery.error
    : chatGroupPhotoVideoAttachmentsError
  const photoVideoAttachmentsLoading = isPinnedMessage
    ? pinnedMessagesQuery.isLoading
    : chatGroupPhotoVideoAttachmentsInitialLoading

  const onShareAttachment = useCallback(async () => {
    setShareLoading(true)
    if (photoVideoAttachments && attachmentIndex !== undefined) {
      const currentAttachment = photoVideoAttachments[attachmentIndex]
      if (currentAttachment) {
        await shareAttachment(currentAttachment)
      }
    }
    setShareLoading(false)
  }, [attachmentIndex, photoVideoAttachments])

  useLayoutEffect(() => {
    if (attachmentIndex === undefined && url) {
      const currentAttachmentIndex = photoVideoAttachments?.findIndex(
        attachment => attachment?.uri === url
      )
      setAttachmentIndex(currentAttachmentIndex)
    }
  }, [attachmentIndex, photoVideoAttachments, url])

  useLayoutEffect(() => {
    if (photoVideoAttachments && !isUndefined(attachmentIndex)) {
      const attachmentUrl = photoVideoAttachments[attachmentIndex]?.uri
      if (attachmentUrl && url !== attachmentUrl) {
        setUrl(attachmentUrl)
      }
    }
  }, [attachmentIndex, photoVideoAttachments, url])

  useLayoutEffect(() => {
    const title =
      attachmentIndexParam &&
      photoVideoAttachments?.length &&
      attachmentIndex !== undefined
        ? `${attachmentIndex + 1} of ${photoVideoAttachments.length}`
        : isPinnedMessage
        ? t('pinnedMediaFiles')
        : t('mediaFiles')
    showHeaderTitle(navigation, title)
  }, [
    attachmentIndex,
    attachmentIndexParam,
    photoVideoAttachments?.length,
    navigation,
    isPinnedMessage,
  ])

  useLayoutEffect(() => {
    if (photoVideoAttachments && attachmentIndex !== undefined) {
      const currentAttachment = photoVideoAttachments[attachmentIndex]
      if (currentAttachment) {
        showShareHeaderButton({
          navigation,
          theme,
          onPress: onShareAttachment,
          loading: isShareLoading,
        })
      }
    }
  }, [
    attachmentIndex,
    isShareLoading,
    navigation,
    onShareAttachment,
    photoVideoAttachments,
    theme,
  ])

  return (
    <StatusBoundary
      isLoading={photoVideoAttachmentsLoading}
      error={photoVideoAttachmentsError}
    >
      <FileView
        attachments={photoVideoAttachments}
        setCurrentIndex={setAttachmentIndex}
        currentIndex={attachmentIndex}
      />
    </StatusBoundary>
  )
}

export function isPdfAttachment(attachment: Attachment) {
  return attachment.mimeType === 'application/pdf'
}
